<template>
  <div class="rules container">
    <header class="rules-head">
      <div>
        <h1 class="rules-title">
          {{ lang === 'zh' ? 'PDGA 规则中心' : 'PDGA Rules Center' }}
        </h1>
        <p class="rules-sub">
          {{ lang === 'zh'
            ? '飞盘高尔夫官方规则，按分类浏览或搜索关键词。'
            : 'Browse PDGA disc golf rules by category, or search by keyword.' }}
        </p>
      </div>
      <div class="lang-switch">
        <button
          class="lang-btn"
          :class="{ active: lang === 'zh' }"
          @click="setLang('zh')"
        >
          中
        </button>
        <button
          class="lang-btn"
          :class="{ active: lang === 'en' }"
          @click="setLang('en')"
        >
          EN
        </button>
      </div>
    </header>

    <div class="rules-search">
      <span class="rules-search-icon">🔍</span>
      <input
        v-model.trim="searchValue"
        type="text"
        class="rules-search-input"
        :placeholder="lang === 'zh' ? '搜索规则关键词（中文或英文）' : 'Search by keyword (English or Chinese)'"
        @input="onSearchInput"
      >
      <button
        v-if="searchValue"
        class="rules-search-clear"
        :aria-label="lang === 'zh' ? '清除' : 'Clear'"
        @click="clearSearch"
      >
        ×
      </button>
    </div>

    <div
      v-if="loading"
      class="rules-state"
    >
      {{ lang === 'zh' ? '加载中...' : 'Loading...' }}
    </div>

    <div
      v-else-if="error"
      class="rules-state rules-state-error"
    >
      {{ error }}
      <button
        class="retry-btn"
        @click="loadRules"
      >
        {{ lang === 'zh' ? '重试' : 'Retry' }}
      </button>
    </div>

    <template v-else>
      <div
        v-if="searchValue"
        class="rules-search-tip"
      >
        <span>🔍</span>
        <span>{{ lang === 'zh'
          ? `找到 ${filteredRules.length} 条结果`
          : `Found ${filteredRules.length} result(s)` }}</span>
        <button
          class="reset-btn"
          @click="clearSearch"
        >
          {{ lang === 'zh' ? '返回' : 'Back' }}
        </button>
      </div>

      <!-- 分类 tabs -->
      <div
        v-if="!searchValue"
        class="rules-tabs"
      >
        <button
          v-for="(cat, idx) in categories"
          :key="cat.id"
          class="tab-item"
          :class="{ active: activeCategory === idx }"
          @click="selectCategory(idx)"
        >
          <span class="tab-name">{{ catName(cat) }}</span>
          <span class="tab-count">{{ ruleCounts[cat.id] || 0 }}</span>
        </button>
      </div>

      <div
        v-if="filteredRules.length === 0"
        class="rules-state"
      >
        {{ lang === 'zh' ? '暂无规则' : 'No rules' }}
      </div>

      <div
        v-else
        class="rules-list"
      >
        <div
          v-for="(rule, idx) in filteredRules"
          :key="rule.id"
          class="rule-card"
          :class="{ expanded: expandedSet.has(rule.id) }"
        >
          <div
            class="rule-head"
            @click="toggleRule(rule.id)"
          >
            <div class="rule-num">
              {{ idx + 1 }}
            </div>
            <div class="rule-title-wrap">
              <div class="rule-title">
                {{ ruleTitle(rule) }}
              </div>
              <div
                v-if="rule.categoryName"
                class="rule-cat"
              >
                🏷 {{ rule.categoryName }}
              </div>
            </div>
            <div class="rule-arrow">
              {{ expandedSet.has(rule.id) ? '▲' : '▼' }}
            </div>
          </div>
          <div
            v-if="expandedSet.has(rule.id)"
            class="rule-body"
          >
            <div class="rule-content">
              {{ ruleContent(rule) }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getAllRules } from '../api/webApi';

export default {
  name: 'RulesView',
  data() {
    return {
      lang: 'zh', // 'zh' | 'en'
      loading: true,
      error: '',
      categories: [], // [{ id, name, nameEn }]
      rulesData: {}, // { categoryId: [rule] }
      ruleCounts: {},
      activeCategory: 0,
      searchValue: '',
      expandedSet: new Set(),
      filteredRules: [],
    };
  },
  watch: {
    lang() {
      // 语言切换时重新过滤
      this.applyFilter();
      try {
        localStorage.setItem('cdgc_rules_lang', this.lang);
      } catch (e) {
        // localStorage 不可用时静默忽略
      }
    },
  },
  async created() {
    try {
      const saved = localStorage.getItem('cdgc_rules_lang');
      if (saved === 'zh' || saved === 'en') this.lang = saved;
    } catch (e) {
      // localStorage 不可用时静默忽略
    }
    await this.loadRules();
  },
  methods: {
    async loadRules() {
      this.loading = true;
      this.error = '';
      try {
        const data = await getAllRules();
        const list = Array.isArray(data) ? data : (data && data.data) || [];
        if (!list.length) {
          this.categories = [];
          this.rulesData = {};
          this.ruleCounts = {};
          this.filteredRules = [];
          return;
        }
        this.categories = list.map((cat) => ({
          id: cat.id,
          name: cat.name,
          nameEn: cat.nameEn || '',
          sortOrder: cat.sortOrder,
        }));
        const data2 = {};
        const counts = {};
        list.forEach((cat) => {
          const rules = (cat.rules || []).map((r) => ({
            id: r.id,
            title: r.title,
            titleEn: r.titleEn || '',
            content: r.content,
            contentEn: r.contentEn || '',
            categoryId: cat.id,
            categoryName: this.catName(cat),
          }));
          data2[cat.id] = rules;
          counts[cat.id] = rules.length;
        });
        this.rulesData = data2;
        this.ruleCounts = counts;
        this.applyFilter();
      } catch (e) {
        console.warn('加载规则失败', e);
        this.error = (this.lang === 'zh' ? '加载失败：' : 'Load failed: ') + (e.message || e);
      } finally {
        this.loading = false;
      }
    },
    setLang(l) {
      this.lang = l;
    },
    catName(cat) {
      if (!cat) return '';
      return this.lang === 'en' ? (cat.nameEn || cat.name) : cat.name;
    },
    ruleTitle(rule) {
      if (!rule) return '';
      return this.lang === 'en' ? (rule.titleEn || rule.title) : rule.title;
    },
    ruleContent(rule) {
      if (!rule) return '';
      return this.lang === 'en' ? (rule.contentEn || rule.content) : rule.content;
    },
    selectCategory(idx) {
      this.activeCategory = idx;
      this.expandedSet = new Set();
      this.applyFilter();
    },
    toggleRule(id) {
      const next = new Set(this.expandedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.expandedSet = next;
    },
    onSearchInput() {
      this.applyFilter();
    },
    clearSearch() {
      this.searchValue = '';
      this.applyFilter();
    },
    applyFilter() {
      if (this.searchValue) {
        const q = this.searchValue.toLowerCase();
        const all = [];
        this.categories.forEach((cat) => {
          const rules = this.rulesData[cat.id] || [];
          rules.forEach((r) => {
            if (
              (r.title || '').toLowerCase().includes(q)
              || (r.content || '').toLowerCase().includes(q)
              || (r.titleEn || '').toLowerCase().includes(q)
              || (r.contentEn || '').toLowerCase().includes(q)
            ) {
              all.push(r);
            }
          });
        });
        this.filteredRules = all;
      } else {
        const cat = this.categories[this.activeCategory];
        this.filteredRules = cat ? (this.rulesData[cat.id] || []) : [];
      }
      // 默认展开当前所有可见规则
      this.expandedSet = new Set(this.filteredRules.map((r) => r.id));
    },
  },
};
</script>

<style scoped>
.rules {
  padding-top: 8px;
  padding-bottom: 32px;
}
.rules-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.rules-title {
  margin: 0;
  font-size: 24px;
}
.rules-sub {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 13px;
}
.lang-switch {
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 3px;
  gap: 2px;
  flex-shrink: 0;
}
.lang-btn {
  border: none;
  background: transparent;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
}
.lang-btn.active {
  background: #fff;
  color: var(--text);
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.rules-search {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0 14px;
  height: 44px;
  margin-bottom: 14px;
}
.rules-search-icon {
  margin-right: 8px;
  color: var(--muted);
  font-size: 14px;
}
.rules-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: var(--text);
}
.rules-search-clear {
  border: none;
  background: transparent;
  font-size: 18px;
  color: var(--muted);
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}
.rules-state {
  padding: 32px 12px;
  text-align: center;
  color: var(--muted);
  font-size: 14px;
}
.rules-state-error {
  color: #dc2626;
}
.retry-btn {
  margin-left: 12px;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: #fff;
  cursor: pointer;
  font-size: 13px;
}
.rules-search-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(0, 190, 190, 0.06);
  border-radius: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: var(--text);
}
.reset-btn {
  margin-left: auto;
  border: none;
  background: transparent;
  color: var(--primary-600, #008c8c);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.rules-tabs {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 0 0 8px;
  margin-bottom: 8px;
  scrollbar-width: thin;
}
.tab-item {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: #fff;
  color: var(--text);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.tab-item:hover {
  border-color: var(--primary);
}
.tab-item.active {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
  font-weight: 600;
}
.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 0 5px;
  height: 18px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  font-size: 11px;
  font-weight: 600;
}
.tab-item.active .tab-count {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}
.rules-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rule-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rule-card:hover {
  border-color: var(--primary);
}
.rule-head {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
}
.rule-num {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(0, 190, 190, 0.12);
  color: var(--primary-600, #008c8c);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
}
.rule-card.expanded .rule-num {
  background: var(--primary);
  color: #fff;
}
.rule-title-wrap {
  min-width: 0;
}
.rule-title {
  font-weight: 600;
  font-size: 15px;
  line-height: 1.45;
  word-break: break-word;
}
.rule-cat {
  margin-top: 4px;
  font-size: 12px;
  color: var(--primary-600, #008c8c);
}
.rule-arrow {
  color: var(--muted);
  font-size: 12px;
}
.rule-body {
  padding: 0 16px 16px 64px;
  border-top: 1px dashed var(--border);
}
.rule-content {
  margin-top: 12px;
  color: var(--text);
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}
@media (max-width: 640px) {
  .rule-body {
    padding-left: 16px;
  }
  .rules-title {
    font-size: 20px;
  }
  .rules-head {
    flex-wrap: wrap;
  }
}
</style>